import {forwardRef, useId, type ComponentPropsWithoutRef, type ReactNode} from 'react';
import {cn} from '../lib/utils';
import {FieldLabel, FieldMessage} from '../lib/field';
import type {Testable} from '../lib/testable';
import {Stack} from '../Stack';

export interface RadioOption {
	value: string;
	label?: ReactNode;
	disabled?: boolean;
}

export interface RadioGroupProps extends Omit<ComponentPropsWithoutRef<'input'>, 'type' | 'className'>, Testable {
	options: readonly (string | RadioOption)[];
	label?: ReactNode;
	error?: ReactNode;
	hint?: ReactNode;
	required?: boolean;
	className?: string;
}

export const RadioGroup = forwardRef<HTMLInputElement, RadioGroupProps>(({
	options,
	label,
	error,
	hint,
	required = false,
	disabled = false,
	name,
	value,
	defaultValue,
	id,
	testId,
	className,
	...props
}, ref) => {
	const generatedId = useId();
	const groupId = id ?? generatedId;
	const labelId = `${groupId}-label`;
	const messageId = `${groupId}-message`;
	const message = error ?? hint;

	return (
		<div data-component='RadioGroupField' className={cn('flex flex-col gap-1 p-4', className)}>
			{label &&
				<span id={labelId} className='block w-fit py-1 pl-2'>
					<FieldLabel required={required} disabled={disabled}>{label}</FieldLabel>
				</span>}
			<div
				data-component='RadioGroup'
				data-testid={testId}
				role='radiogroup'
				aria-labelledby={label ? labelId : undefined}
				aria-describedby={message ? messageId : undefined}
				aria-required={required || undefined}
				aria-invalid={error ? true : undefined}
				className='flex flex-col items-start gap-2'>
				{options.map(normalize).map(option => (
					<Radio
						key={option.value}
						{...props}
						{...(value === undefined ? {} : {checked: value === option.value})}
						{...(defaultValue === undefined ? {} : {defaultChecked: defaultValue === option.value})}
						ref={ref}
						id={`${groupId}-${option.value}`}
						// The shared name is what makes the radios exclusive, so it cannot be optional —
						// register() supplies one, a standalone group falls back to its own id.
						name={name ?? groupId}
						value={option.value}
						label={option.label}
						invalid={Boolean(error)}
						disabled={disabled || option.disabled}/>
				))}
			</div>
			{message &&
				<div className='pl-2'>
					<FieldMessage id={messageId} error={Boolean(error)}>{message}</FieldMessage>
				</div>}
		</div>
	);
});

RadioGroup.displayName = 'RadioGroup';

const normalize = (option: string | RadioOption): RadioOption =>
	typeof option === 'string' ? {value: option, label: option} : {...option, label: option.label ?? option.value};

interface RadioProps extends Omit<ComponentPropsWithoutRef<'input'>, 'type' | 'className'> {
	label?: ReactNode;
	invalid?: boolean;
}

const Radio = forwardRef<HTMLInputElement, RadioProps>(({
	label,
	invalid = false,
	disabled = false,
	id,
	...props
}, ref) => (
	<Stack as='label' htmlFor={id} gap={3} paddingLeft={2} paddingRight={4} className={rowStyles}>
		<span className='relative inline-flex'>
			<input
				ref={ref}
				id={id}
				type='radio'
				data-component='Radio'
				disabled={disabled}
				className={cn(boxStyles, invalid ? 'border-tone-red' : hoverBorderStyles)}
				{...props}/>
			<span aria-hidden className={dotStyles}/>
		</span>
		{label && <FieldLabel disabled={disabled}>{label}</FieldLabel>}
	</Stack>
));

Radio.displayName = 'Radio';

const rowStyles = 'w-fit cursor-pointer has-[:disabled]:cursor-not-allowed';

const boxStyles =
	'peer size-[16px] shrink-0 cursor-pointer appearance-none rounded-full border border-foreground-300 ' +
	'bg-transparent transition-colors checked:border-brand-main-light focus-visible:outline-none ' +
	'disabled:cursor-not-allowed disabled:border-foreground-300 disabled:bg-background-500';

const hoverBorderStyles = '[&:hover:not(:checked):not(:disabled)]:border-foreground-200';

const dotStyles =
	'pointer-events-none absolute inset-0 m-auto hidden size-[8px] rounded-full bg-brand-main ' +
	'peer-checked:block peer-disabled:bg-foreground-white/60';
